import { useAuthStore } from '@/stores/auth'

const BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'

/**
 * Paths that, when receiving a 401, should NOT trigger the global "logout +
 * redirect" reaction. The login endpoint has its own UX (toast "Email ou
 * senha inválidos" rendered by the page) and would loop forever if we
 * redirected on every wrong password.
 */
const UNAUTH_BYPASS_PATHS = new Set<string>(['/admin/auth/login'])

/**
 * Triggered when an authenticated request returns 401. Clears the auth
 * store and pushes the user back to /login. Pure side-effect — the
 * triggering call still rejects with `ApiError` so the caller can decide
 * whether to show a toast or fall through.
 */
function onSessionExpired(): void {
  // Avoid infinite redirect loops if we're already on /login.
  if (typeof window !== 'undefined') {
    useAuthStore.getState().clearAuth()
    if (window.location.pathname !== '/login') {
      window.location.assign('/login')
    }
  }
}

export class ApiError extends Error {
  override readonly name = 'ApiError'
  constructor(
    public status: number,
    public bodyText: string,
    public bodyJson: unknown = null,
  ) {
    super(`API ${status}: ${bodyText}`)
  }
}

/**
 * Minimal fetch wrapper that injects the JWT from the Zustand auth store
 * and parses JSON responses. Story 1.7 adds a 401 interceptor that clears
 * the store and bounces to `/login` — except on the login route itself,
 * which surfaces 401s back to the caller for UX handling.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers ?? {}),
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let json: unknown = null
    try {
      json = text === '' ? null : JSON.parse(text)
    } catch {
      json = null
    }
    if (res.status === 401 && !UNAUTH_BYPASS_PATHS.has(path)) {
      onSessionExpired()
    }
    throw new ApiError(res.status, text, json)
  }

  // Allow callers to opt out of JSON parsing by returning undefined
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
